import React, { useCallback, useEffect } from 'react'
import { UseFormMethods } from 'react-hook-form'
import get from 'lodash/get'
import { stringifyUrl } from 'query-string'
import { ControlledPhoneInput, ControlledTextField } from 'transfix-form/components'
import AddIcon from 'transfix-icons/build/AddIcon'
import Box from 'transfix-ui/core/Box'
import Grid from 'transfix-ui/core/Grid'
import Typography from 'transfix-ui/core/Typography'
import { makeStyles } from 'transfix-ui/core/styles'
import { ICarrierUserTypes, IUser, Maybe } from 'types/graphqlTypes'
import { ICoverInfoCarrier } from 'apps/marketplace/components/CoverModal/types'
import { Button } from 'transfix-ui'
import ControlledCarrierUserAutosuggest from '../../../../../../pages/shipment-details/components/forms/controlled/ControlledCarrierUserAutosuggest'
import { IShipmentCoverActorFragment } from '../../../../graphql/ShipmentActorFragment'
import ControlledTrailerNumberAutocomplete from '../../../controlled/ControlledTrailerNumberAutocomplete'
import ControlledTruckNumberAutocomplete from '../../../controlled/ControlledTruckNumberAutocomplete'
import EmptyLocationETAGroup from './EmptyLocationETAGroup'

const useAssignDriverNowInputGroupStyles = makeStyles(theme => ({
  buttonWrapper: {
    marginTop: theme.spacing(1),
  },
  fieldWrapper: {
    marginTop: theme.spacing(2),
  },
}))

interface IAssignDriverNowInputGroupProps
  extends Pick<UseFormMethods, 'control' | 'setValue' | 'errors'> {
  selectedCarrier: Maybe<ICoverInfoCarrier>
  driver: Maybe<IShipmentCoverActorFragment>
  pickupTime: string
  shipmentTimezone: string
}

export const AssignDriverNowInputGroup = ({
  control,
  driver,
  errors,
  pickupTime,
  selectedCarrier,
  setValue,
  shipmentTimezone,
}: IAssignDriverNowInputGroupProps) => {
  const classes = useAssignDriverNowInputGroupStyles()

  const {
    dotNumber: carrierDotNumber,
    id: carrierId,
    mcNumber: carrierMcNumber,
    name: carrierName,
  } = selectedCarrier || { id: '' }

  const driverSelectHandler = useCallback(
    (driver: IUser | null) => {
      const { cellPhone } = driver || {}
      setValue('driver', driver, { shouldValidate: true })
      setValue('driverContactPhone', cellPhone || '', { shouldValidate: true })
    },
    [setValue]
  )

  const addNewUserHandler = useCallback(() => {
    const addUserUrl = stringifyUrl({
      url: `${__ROOT__}/users/new`,
      query: {
        orgType: 'carrier',
        orgId: carrierId || '',
        orgName: carrierName || '',
        orgMcNumber: carrierMcNumber || '',
        orgDotNumber: carrierDotNumber || '',
      },
    })
    window.open(addUserUrl, '_blank')
  }, [carrierId, carrierName, carrierMcNumber, carrierDotNumber])

  /**
   * This useEffect will update the phone number on driver change
   */
  useEffect(() => {
    const cellPhone = get(driver, 'cellPhone', '')
    setValue('driverContactPhone', cellPhone)
  }, [driver, setValue])

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Typography variant='subtitle2'>Driver Information</Typography>
      </Grid>
      <Grid item xs={6}>
        <div data-test='sd-coverage-form-driver'>
          <ControlledCarrierUserAutosuggest
            name='driverId'
            carrierId={carrierId}
            control={control}
            user={driver}
            userTypes={[ICarrierUserTypes.driver, ICarrierUserTypes.owner_operator]}
            onUserChange={driverSelectHandler}
            AutocompleteProps={{
              label: 'Driver Name',
              size: 'medium',
              placeholder: 'Select a driver',
              renderInputType: undefined,
              required: true,
              inputError: !!errors.driverId,
            }}
          />
        </div>
        <div className={classes.buttonWrapper}>
          <Button variant='text' startIcon={<AddIcon />} onClick={addNewUserHandler} noPadding>
            Add User
          </Button>
        </div>
      </Grid>
      <Grid item xs={12}>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <ControlledPhoneInput
              control={control}
              name='driverContactPhone'
              PhoneInputProps={{
                dataTest: 'sd-coverage-form-driver-phone',
                label: 'Driver Phone',
                clearable: false,
                invalid: Boolean(errors.driverContactPhone),
                large: true,
              }}
            />
          </Grid>
          <Grid item xs={6} data-test='sd-coverage-form-truck-number'>
            <ControlledTruckNumberAutocomplete
              control={control}
              name='truckNumber'
              carrierApplicationId={carrierId}
              TruckAutocompleteProps={{
                AutocompleteProps: {
                  size: 'medium',
                  label: 'Truck #',
                  placeholder: 'Truck Number',
                  inputError: !!errors.truckNumber,
                  inputValidationMessage: errors.truckNumber?.message && '',
                },
              }}
            />
          </Grid>
          <Grid item xs={6}>
            <ControlledTrailerNumberAutocomplete
              name='trailerNumber'
              control={control}
              carrierApplicationId={carrierId}
              TrailerAutocompleteProps={{
                AutocompleteProps: {
                  label: 'Trailer #',
                  placeholder: 'Trailer Number',
                  inputError: Boolean(errors.trailerNumber),
                  inputValidationMessage: errors.trailerNumber?.message && '',
                },
                dataTest: 'sd-coverage-form-trailer-number',
              }}
            />
          </Grid>
          <Grid item xs={6}>
            <ControlledTextField
              control={control}
              name='vinNumber'
              TextInputProps={{
                label: 'Tractor VIN # (Last 4 to 6)',
                dataTest: 'sd-coverage-form-vin-number',
                placeholder: 'Tractor VIN # (Last 4 to 6)',
                error: Boolean(errors.vinNumber),
                validationMessage: errors.vinNumber?.message && '',
                large: true,
              }}
            />
          </Grid>
        </Grid>
      </Grid>
      <Grid item xs={12}>
        <Box pt={2}>
          <EmptyLocationETAGroup
            control={control}
            errors={errors}
            pickupTime={pickupTime}
            shipmentTimezone={shipmentTimezone}
          />
        </Box>
      </Grid>
    </Grid>
  )
}

export default AssignDriverNowInputGroup
